import { Component, OnDestroy, OnInit } from '@angular/core';
import {
	UntypedFormBuilder,
	UntypedFormGroup,
	Validators,
} from '@angular/forms';
import {
	DateAdapter,
	MAT_DATE_FORMATS,
	MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
	MAT_MOMENT_DATE_ADAPTER_OPTIONS,
	MomentDateAdapter,
} from '@angular/material-moment-adapter';
import { ActivatedRoute, Router } from '@angular/router';

import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Store } from '@ngrx/store';
import moment from 'moment';

import { AddressConstants } from '../../../../modules/address/address.constants';
import { AddressService } from '../../../../modules/address/address.service';
import { BeneficiariesService } from '../../../../modules/beneficiaries/beneficiaries.service';
import { BeneficiaryDependentUpdateDto } from '../../../../modules/beneficiaries/dtos/beneficiary-dependent-update';
import { Gender } from '../../../../shared/enums/gender.enum';
import { MARITAL_STATUS } from '../../../../shared/models/marital-status';
import { PT_BR_DATE_FORMATS } from '../../../../utils/date-formats.utils';
import { EnumUtils } from '../../../../utils/enum.utils';
import { StringUtils } from '../../../../utils/string.utils';
import { ProductsConfigHelper } from '../../../products/products.config';
import {
	GroupsListSummary,
	OrgListSummary,
	MessageResponse,
} from '../../models/organizations.model';
import { PositionSummary } from '../../models/positions.model';
import { ProductsSummary } from '../../models/products.model';
import { OrganizationsLegacyService } from '../../organizations-legacy.service';
import {
	fetchAddressByCEPStart,
	fetchAuxUFCitiesClear,
	fetchUFCitiesClear,
	fetchOrgPositionsStart,
	restoreFormReset,
	editBeneficiaryClearCompleAct,
} from '../../state/organizations.actions';
import {
	selectAddressResult,
	selectAuxCities,
	selectCities,
	selectNewBeneCpf,
	selectPositions,
} from '../../state/organizations.selector';
import { OrgGroupModalComponent } from '../user-registration/org-group-modal/org-group-modal.component';
import { OrgListModalComponent } from '../user-registration/org-list-modal/org-list-modal.component';

import { ManageDependentModalComponent } from './manage-dependent-modal/manage-dependent-modal.component';

@Component({
	providers: [
		{ provide: MAT_DATE_LOCALE, useValue: 'pt-BR' },
		{
			deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
			provide: DateAdapter,
			useClass: MomentDateAdapter,
		},
		{ provide: MAT_DATE_FORMATS, useValue: PT_BR_DATE_FORMATS },
	],
	selector: 'app-beneficiary-registration',
	styleUrls: ['./beneficiary-registration.component.scss'],
	templateUrl: './beneficiary-registration.component.html',
})
export class BeneficiaryRegistrationComponent implements OnInit, OnDestroy {
	addForm: UntypedFormGroup;
	auxCities: string[] = [];
	auxStateForm: boolean = true;
	auxStateForm2: boolean = true;
	beneficiaryId: string = '';
	cepPattern = '[0-9]{8}';
	cities: string[] = [];
	cpfPattern = '[0-9]{11}';
	cpfTitular = '';
	dependents: BeneficiaryDependentUpdateDto[] = [];
	dialogRef!: MatDialogRef<
		| ManageDependentModalComponent
		| OrgGroupModalComponent
		| OrgListModalComponent
	>;
	disableSaveBtn = false;
	genders: Gender[] = Object.values(Gender);
	groups: GroupsListSummary[] = [];
	inputId: string = String(Math.random());
	isLoading: boolean = false;
	isLoadingCities = false;
	isLoadingWorkCities = false;
	maritalStatusList = MARITAL_STATUS;
	organizationId: string = '';
	orgs: OrgListSummary[] = [];
	positions: PositionSummary[] = [];
	prfForm: UntypedFormGroup;
	products: ProductsSummary[] = [];
	prsForm: UntypedFormGroup;
	regStep = 1;

	uf = AddressConstants.ufLabels;
	wAddForm: UntypedFormGroup;

	private onSubmitSubscription$: Subscription = new Subscription();

	private readonly unsubscribe$: Subject<void> = new Subject();

	constructor(
		private readonly activatedRoute: ActivatedRoute,
		private readonly beneficiariesService: BeneficiariesService,
		private readonly dialog: MatDialog,
		private readonly fb: UntypedFormBuilder,
		private readonly productsConfigHelper: ProductsConfigHelper,
		private readonly router: Router,
		private readonly store: Store,
		private readonly organizationsService: OrganizationsLegacyService,
		private readonly addressService: AddressService,
	) {
		this.prsForm = this.fb.group({
			active: [true, [Validators.required]],
			birthday: [null, [Validators.required]],
			gender: [null],
			marital_status: [null],
			mothers_name: [null],
		});
		this.addForm = this.fb.group({
			city: [null],
			complement: [null],
			neighborhood: [null],
			number: [null],
			postal_code: [null, [Validators.pattern(this.cepPattern)]],
			state: [null],
			street: [null],
		});
		this.addForm.get('state')!.disable();
		this.addForm.get('city')!.disable();
		this.prfForm = this.fb.group({
			admission_date: [null],
			plan_inclusion_date: [null],
			position_id: [null],
			registration: [null],
			wage: [null],
		});
		this.wAddForm = this.fb.group({
			city: [null],
			state: [null],
		});
	}

	capitalize(str: string): string {
		return StringUtils.capitalize(str);
	}

	disabledSaveBtn(): boolean {
		return (
			this.prsForm.invalid ||
			this.addForm.invalid ||
			this.wAddForm.invalid ||
			this.prfForm.invalid
		);
	}

	getDepType(dt: number | string): string {
		const kinshipEnum =
			typeof dt === 'number' ? EnumUtils.getKinshipFromNumberValue(dt) : dt;

		return EnumUtils.getKinship(kinshipEnum) ?? '';
	}

	getRequiredAttributesOwner(products: ProductsSummary[]): string[] {
		const ownerAttributes: string[] = [];
		products.forEach((product) => {
			if (product.id) {
				const productConfig = this.productsConfigHelper.findById(product.id);

				if (productConfig?.requiredAttributesOwner) {
					ownerAttributes.push(...productConfig.requiredAttributesOwner);
				}
			}
		});

		return ownerAttributes;
	}

	ngOnDestroy(): void {
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
		this.onSubmitSubscription$.unsubscribe();
		this.store.dispatch(editBeneficiaryClearCompleAct());
		this.store.dispatch(fetchUFCitiesClear());
		this.store.dispatch(fetchAuxUFCitiesClear());
		this.store.dispatch(restoreFormReset());
	}

	ngOnInit(): void {
		this.activatedRoute.params.subscribe(({ id, bId }) => {
			this.organizationId = id;
			this.beneficiaryId = bId;
			this.store
				.select(selectNewBeneCpf)
				.pipe(takeUntil(this.unsubscribe$))
				.subscribe((cpf) => {
					if (cpf) {
						this.cpfTitular = cpf;
					}
				});
			this.store
				.select(selectPositions)
				.pipe(takeUntil(this.unsubscribe$))
				.subscribe((pos) => {
					if (pos) {
						this.positions = pos;
					}
				});
			this.store
				.select(selectCities)
				.pipe(takeUntil(this.unsubscribe$))
				.subscribe((cities) => {
					if (cities.length) {
						this.addForm.get('city')!.enable();
					}
					this.cities = cities;
				});
			this.store
				.select(selectAuxCities)
				.pipe(takeUntil(this.unsubscribe$))
				.subscribe((cities) => {
					if (cities.length) {
						this.wAddForm.get('city')!.enable();
					}
					this.auxCities = cities;
				});
			this.store.dispatch(fetchOrgPositionsStart({ id: this.organizationId }));
		});
	}

	onAddDependent(): void {
		this.dialogRef = this.dialog.open(ManageDependentModalComponent, {
			data: {
				cpf: this.cpfTitular,
				dep: null,
				editing: -1,
				orgId: this.organizationId,
				products: this.products,
			},
			disableClose: true,
		});
		this.dialogRef.afterClosed().subscribe((result) => {
			if (result) {
				this.dependents.push(result.request);
			}
		});
	}

	onCepBlur(): void {
		if (
			this.addForm.get('postal_code')?.value &&
			this.addForm.get('postal_code')!.valid
		) {
			const cep: string = this.addForm.get('postal_code')!.value;
			this.store.dispatch(fetchAddressByCEPStart({ cep }));
			this.store.select(selectAddressResult).subscribe((result) => {
				if (result?.error) {
					this.store.dispatch(fetchUFCitiesClear());
					this.auxStateForm = true;
					this.addForm.get('state')!.setValue(null);
					this.addForm.get('city')!.setValue(null);
					this.addForm.get('state')!.enable();
				} else {
					this.auxStateForm = false;
					this.addForm.get('street')!.setValue(result?.street);
					this.addForm.get('neighborhood')!.setValue(result?.neighborhood);
					this.addForm.get('state')!.setValue(result?.state);
					this.addForm.get('city')!.setValue(result?.city);
					this.addForm.get('state')!.disable();
					this.addForm.get('city')!.disable();
				}
			});
		}
	}

	onDeleteDependent(index: number): void {
		this.dependents.splice(index, 1);
	}

	onEditDependent(dep: BeneficiaryDependentUpdateDto, editing: number): void {
		this.dialogRef = this.dialog.open(ManageDependentModalComponent, {
			data: { cpf: this.cpfTitular, dep, editing, orgId: this.organizationId },
			disableClose: true,
		});
		this.dialogRef.afterClosed().subscribe((result) => {
			if (result) {
				this.dependents[result.idx] = result.request;
			}
		});
	}

	onSubmit(): void {
		this.isLoading = true;

		const request = this.processForm();

		if (request) {
			this.onSubmitSubscription$ = this.beneficiariesService
				.updateOne({
					beneficiaryId: this.beneficiaryId,
					dto: request,
					organizationId: this.organizationId,
				})
				.subscribe(
					async (response: MessageResponse) => {
						this.isLoading = false;
						this.organizationsService.showSnack(
							response?.message,
							response?.type,
						);
						await this.router.navigate([
							'organizacoes',
							this.organizationId,
							'beneficiarios',
							this.beneficiaryId,
						]);
					},
					(error) => {
						this.isLoading = false;
						this.organizationsService.showSnack(error.error?.message, 'error');
					},
				);
		}
	}

	onUfChange(id: number, group?: 'address' | 'work'): void {
		const workCityInput = this.wAddForm.get('city');
		const cityInput = this.addForm.get('city');

		if (group === 'address') {
			cityInput!.setValue(null);
			cityInput!.disable();
			this.isLoadingCities = true;
		} else {
			workCityInput!.setValue(null);
			workCityInput!.disable();
			this.isLoadingWorkCities = true;
		}

		this.addressService.getCitiesByUfId(id).subscribe({
			error: () => {
				this.isLoadingWorkCities = false;
				this.isLoadingCities = false;

				if (group === 'address') {
					this.auxStateForm = false;
				} else {
					this.auxStateForm2 = false;
				}
			},
			next: (response) => {
				const cities = this.addressService.organizeCitiesByUfId(id, response);

				if (group === 'address') {
					this.cities = cities;
					this.auxStateForm = true;
				} else {
					this.auxCities = cities;
					this.auxStateForm2 = true;
				}

				this.isLoadingWorkCities = false;
				this.isLoadingCities = false;
			},
		});

		workCityInput!.enable();
		cityInput!.enable();
	}

	processForm(): any {
		if (
			this.prsForm.invalid ||
			this.addForm.invalid ||
			this.wAddForm.invalid ||
			this.prfForm.invalid
		) {
			return null;
		}
		this.addForm.get('state')!.enable();
		this.addForm.get('city')!.enable();
		const cfg = this.prsForm.value;
		const add = this.addForm.value;
		const wAdd = this.wAddForm.value;
		const prf = this.prfForm.value;
		this.addForm.get('state')!.disable();
		this.addForm.get('city')!.disable();
		const request: any = {
			id: this.beneficiaryId,
			...cfg,
			address: add,
			city_work: wAdd.city,
			professional_data: prf,
			state_work: wAdd.state,
		};
		request.birthday = moment(request.birthday).format('YYYY-MM-DD hh:mm:ssZZ');
		request.professional_data.admission_date = request.professional_data
			.admission_date
			? moment(request.professional_data.admission_date).format(
					'YYYY-MM-DD hh:mm:ssZZ',
				)
			: null;
		request.professional_data.plan_inclusion_date = request.professional_data
			.plan_inclusion_date
			? moment(request.professional_data.plan_inclusion_date).format(
					'YYYY-MM-DD hh:mm:ssZZ',
				)
			: null;

		request.dependents = this.dependents;

		return request;
	}

	processLabelCase(label: string) {
		return label.replace(/\w\S*/g, (txt: string) => {
			return txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase();
		});
	}

	updateValidation(): void {
		for (const control in this.prsForm.controls) {
			this.prsForm.get(control)!.markAsTouched();
			this.prsForm.get(control)!.updateValueAndValidity({ onlySelf: true });
		}

		for (const control in this.addForm.controls) {
			this.addForm.get(control)!.markAsTouched();
			this.addForm.get(control)!.updateValueAndValidity({ onlySelf: true });
		}

		for (const control in this.wAddForm.controls) {
			this.wAddForm.get(control)!.markAsTouched();
			this.wAddForm.get(control)!.updateValueAndValidity({ onlySelf: true });
		}

		for (const control in this.prfForm.controls) {
			this.prfForm.get(control)!.markAsTouched();
			this.prfForm.get(control)!.updateValueAndValidity({ onlySelf: true });
		}
	}
}
